import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Trash2,
  HandIcon,
  RefreshCw,
  LinkIcon,
  Edit2,
  ChevronLeft,
  ChevronRight,
  PuzzleIcon,
  Layers,
  ShoppingCart,
  Copy,
  Pin,
  PinOff,
  Tag,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { Card, CardContent, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  productService,
  Product,
  ProductRelation,
  RelationshipType,
} from '../../services/productService';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  formatDateForDisplay,
  formatProductPrice,
  type NumberFormat,
  type DateFormat,
} from '@/utils/numberFormat';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import EnhancedAddRelatedProductPanel from '@/components/products/EnhancedAddRelatedProductPanel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { pickPrimaryImage } from '@/utils/images';
import { useOrgSettings } from '@/hooks/useOrgSettings';

import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { logError } from '@/utils/errorHandler';

// Helper to format relationship type for display
const formatRelationshipType = (
  type: RelationshipType,
  getTabsLabels: ReturnType<typeof useProductDetailPageTranslations>['getTabsLabels']
): string => {
  if (!getTabsLabels?.actions?.relatedProducts?.relationshipTypes) {
    // Fallback to hardcoded English if translations aren't available
    switch (type) {
      case 'accessory':
        return 'Accessory';
      case 'variant':
        return 'Variant';
      case 'frequently_bought_together':
        return 'Bought Together';
      case 'replacement':
        return 'Replacement';
      case 'similar':
        return 'Similar';
      case 'general':
        return 'General';
      default:
        return 'Related';
    }
  }

  switch (type) {
    case 'accessory':
      return getTabsLabels.actions.relatedProducts.relationshipTypes.accessory;
    case 'variant':
      return getTabsLabels.actions.relatedProducts.relationshipTypes.variant;
    case 'frequently_bought_together':
      return getTabsLabels.actions.relatedProducts.relationshipTypes.frequently_bought_together;
    case 'replacement':
      return getTabsLabels.actions.relatedProducts.relationshipTypes.replacement;
    case 'similar':
      return getTabsLabels.actions.relatedProducts.relationshipTypes.similar;
    case 'general':
      return getTabsLabels.actions.relatedProducts.relationshipTypes.general;
    default:
      return getTabsLabels.actions.relatedProducts.relationshipTypes.related;
  }
};

// Helper to get relationship type icon
const getRelationshipTypeIcon = (type: RelationshipType) => {
  switch (type) {
    case 'accessory':
      return <PuzzleIcon className="size-3.5" />;
    case 'variant':
      return <Layers className="size-3.5" />;
    case 'frequently_bought_together':
      return <ShoppingCart className="size-3.5" />;
    case 'replacement':
      return <RefreshCw className="size-3.5" />;
    case 'similar':
      return <Copy className="size-3.5" />;
    case 'general':
      return <LinkIcon className="size-3.5" />;
    default:
      return <LinkIcon className="size-3.5" />;
  }
};

// Helper to get relationship type badge style
const getRelationshipTypeStyle = (type: RelationshipType) => {
  switch (type) {
    case 'accessory':
      return 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800/30';
    case 'variant':
      return 'bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-300 dark:border-indigo-800/30';
    case 'frequently_bought_together':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800/30';
    case 'replacement':
      return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800/30';
    case 'similar':
      return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800/30';
    case 'general':
      return 'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800/30 dark:text-slate-300 dark:border-slate-700/30';
    default:
      return 'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800/30 dark:text-slate-300 dark:border-slate-700/30';
  }
};

interface RelatedProductsPanelProps {
  productId: number;
  onRefresh?: () => void;
}

interface EnhancedProduct extends Product {
  relation?: ProductRelation;
}

const RelatedProductsPanel: React.FC<RelatedProductsPanelProps> = ({
  productId,
  onRefresh: _onRefresh,
}) => {
  // i18n: Get translations hook
  const { getTabsLabels } = useProductDetailPageTranslations();

  const [relatedProducts, setRelatedProducts] = useState<EnhancedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [editingRelation, setEditingRelation] = useState<ProductRelation | null>(null);
  const [relationshipType, setRelationshipType] = useState<RelationshipType>('general');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const maxProductsPerPage = 8;
  const currentPage = Math.floor(activeIndex / maxProductsPerPage) + 1;
  const totalPages = Math.ceil(relatedProducts.length / maxProductsPerPage);

  // Move fetchRelatedProducts declaration before useEffect
  const fetchRelatedProducts = useCallback(async () => {
    if (!productId) return;

    setLoading(true);
    setError(null);

    try {
      // 1. Get all related products (explicit + category matches)
      const products = await productService.getRelatedProducts(productId);

      // 2. Get explicit relationships to identify which products are explicitly related
      const explicitRelations = await productService
        .getExplicitRelations(productId)
        .catch(() => []);

      // Create a map of related product IDs to their relation details
      const relationsMap = new Map<number, ProductRelation>();
      explicitRelations.forEach(relation => {
        relationsMap.set(relation.related_product_id, relation);
      });

      // ---------------------------------------------------------------
      // OPTIMIZED: Only fetch assets for visible products to improve performance
      // Assets will be loaded lazily when products come into view
      // ---------------------------------------------------------------
      const enhancedProducts: EnhancedProduct[] = products.map(prod => ({
        ...prod,
        relation: relationsMap.get(prod.id as number),
      }));

      // Sort: pinned first, then explicitly related (manual), then algorithmic
      enhancedProducts.sort((a, b) => {
        // First sort by pinned status
        if (a.relation?.is_pinned && !b.relation?.is_pinned) return -1;
        if (!a.relation?.is_pinned && b.relation?.is_pinned) return 1;

        // For pinned items, maintain the order by created date (newest first)
        if (a.relation?.is_pinned && b.relation?.is_pinned) {
          return (
            new Date(b.relation.created_at).getTime() - new Date(a.relation.created_at).getTime()
          );
        }

        // Then sort by relation existence (explicit relations first)
        if (a.relation && !b.relation) return -1;
        if (!a.relation && b.relation) return 1;

        // Then sort by source (manual first)
        if (a.relation?.source === 'manual' && b.relation?.source !== 'manual') return -1;
        if (a.relation?.source !== 'manual' && b.relation?.source === 'manual') return 1;

        // Finally sort by name
        return (a.name || '').localeCompare(b.name || '');
      });

      setRelatedProducts(enhancedProducts);
    } catch {
      // Use stable error key instead of translated message to avoid dependency issues
      setError('FETCH_FAILED');
      setRelatedProducts([]);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  // Load related products when component mounts or productId changes
  useEffect(() => {
    if (!productId) {
      setRelatedProducts([]);
      return;
    }

    fetchRelatedProducts();
  }, [productId, fetchRelatedProducts]);

  // Handle adding a new related product - Now with dialog for notes
  const handleAddRelatedProduct = async (
    product: Product,
    relType: RelationshipType = 'general',
    notes: string = ''
  ) => {
    // Ensure product has all required properties for display
    const newProduct: EnhancedProduct = {
      ...product,
      name:
        product.name ||
        getTabsLabels?.actions?.relatedProducts?.panel?.unknownProduct ||
        'Unknown Product',
      sku: product.sku || '',
      is_active: true,
      relation: {
        id: 0, // Temporary ID
        product_id: productId,
        related_product_id: product.id as number,
        relationship_type: relType,
        is_pinned: false, // Default to not pinned
        created_at: new Date().toISOString(),
        source: 'manual',
        notes: notes,
      },
    };

    // Update the state with the new product
    setRelatedProducts(prev => {
      const updated = [newProduct, ...prev];
      return updated;
    });

    // Call API to add the relation
    try {
      const success = await productService.toggleRelatedProduct(
        productId,
        product.id!,
        false, // Not pinned by default
        relType, // Pass relationship type directly
        notes // Pass notes
      );

      if (!success) {
        toast.error(
          getTabsLabels?.actions?.relatedProducts?.notifications?.addRelatedFailed ||
            'Failed to add related product'
        );
        // Revert the UI change on failure
        fetchRelatedProducts();
      } else {
        toast.success(
          getTabsLabels?.actions?.relatedProducts?.notifications?.addRelatedSuccess ||
            'Product added to related products'
        );

        // Refresh to get latest relationship data (no timeout needed)
        fetchRelatedProducts();
      }
    } catch {
      toast.error(
        getTabsLabels?.actions?.relatedProducts?.notifications?.addRelatedFailed ||
          'Failed to add related product'
      );
      fetchRelatedProducts();
    }
  };

  // Handle adding multiple related products
  const handleAddMultipleRelatedProducts = async (
    productIds: number[],
    relType: RelationshipType = 'general',
    notes: string = ''
  ) => {
    if (!productIds.length) return { success: false, processed: 0, failed: 0 };

    try {
      // Call the API to add multiple related products
      const result = await productService.addMultipleRelatedProducts(
        productId,
        productIds,
        relType,
        notes
      );

      if (result.success) {
        toast.success(
          getTabsLabels?.actions?.relatedProducts?.notifications?.addRelatedSuccess ||
            `Added ${result.processed} related products`
        );
        if (result.failed > 0) {
          toast.warning(`Failed to add ${result.failed} products`);
        }
      } else {
        toast.error(
          getTabsLabels?.actions?.relatedProducts?.notifications?.addMultipleFailed ||
            'Failed to add related products'
        );
      }

      // Refresh the list to get accurate data
      fetchRelatedProducts();

      return result;
    } catch {
      toast.error(
        getTabsLabels?.actions?.relatedProducts?.notifications?.addMultipleFailed ||
          'Failed to add related products'
      );
      return { success: false, processed: 0, failed: productIds.length };
    }
  };

  // Handle removing a related product
  const handleRemoveRelatedProduct = async (productToRemove: EnhancedProduct) => {
    if (!productToRemove.id) {
      toast.error(
        getTabsLabels?.actions?.relatedProducts?.notifications?.invalidProductRemoval ||
          'Invalid product selected for removal'
      );
      return;
    }

    // Can only remove explicitly related products
    if (!productToRemove.relation) {
      toast.info(
        "This product is shown as related because it shares the same category. It can't be removed."
      );
      return;
    }

    // Optimistically update UI
    setRelatedProducts(prev => prev.filter(product => product.id !== productToRemove.id));

    try {
      // Call API to remove the relation
      const success = await productService.removeRelatedProduct(productId, productToRemove.id);

      if (!success) {
        toast.error(
          getTabsLabels?.actions?.relatedProducts?.notifications?.relationshipRemoveFailed ||
            "Couldn't remove product"
        );
        fetchRelatedProducts();
      }
    } catch {
      toast.error(
        getTabsLabels?.actions?.relatedProducts?.notifications?.relationshipRemoveFailed ||
          "Couldn't remove product"
      );
      fetchRelatedProducts();
    }
  };

  // Handle updating relationship type
  const handleUpdateRelationship = async () => {
    if (!editingRelation) return;

    setSaving(true);

    try {
      const success = await productService.updateRelationship(
        productId,
        editingRelation.related_product_id,
        {
          relationship_type: relationshipType,
          notes: notes,
          is_pinned: editingRelation.is_pinned,
        }
      );

      if (success) {
        // Update local state
        setRelatedProducts(prev =>
          prev.map(product => {
            if (product.id === editingRelation.related_product_id) {
              return {
                ...product,
                relation: product.relation
                  ? {
                      ...product.relation,
                      relationship_type: relationshipType,
                      notes: notes,
                    }
                  : undefined,
              };
            }
            return product;
          })
        );

        toast.success(
          getTabsLabels?.actions?.relatedProducts?.notifications?.relationshipUpdated ||
            'Relationship updated'
        );
        setEditingRelation(null);
      } else {
        toast.error(
          getTabsLabels?.actions?.relatedProducts?.notifications?.relationshipUpdateFailed ||
            'Failed to update relationship'
        );
      }
    } catch {
      toast.error(
        getTabsLabels?.actions?.relatedProducts?.notifications?.relationshipUpdateFailed ||
          'Failed to update relationship'
      );
    } finally {
      setSaving(false);
    }
  };

  // Set up edit relation
  const setupEditRelation = (relation: ProductRelation) => {
    setEditingRelation(relation);
    setRelationshipType(relation.relationship_type);
    setNotes(relation.notes || '');

    // Force a re-render to ensure the popover opens
    setTimeout(() => {
      setEditingRelation({ ...relation });
    }, 10);
  };

  // Navigate between pages
  const handlePrevPage = () => {
    const newIndex = Math.max(0, activeIndex - maxProductsPerPage);
    setActiveIndex(newIndex);
  };

  const handleNextPage = () => {
    const newIndex = Math.min(
      activeIndex + maxProductsPerPage,
      relatedProducts.length - (relatedProducts.length % maxProductsPerPage || maxProductsPerPage)
    );
    setActiveIndex(newIndex);
  };

  // Calculate visible products and navigation state
  const visibleProducts = relatedProducts.slice(activeIndex, activeIndex + maxProductsPerPage);

  // Handle pinning/unpinning a related product
  const handleTogglePin = async (productToUpdate: EnhancedProduct, isPinned: boolean) => {
    if (!productToUpdate.id || !productToUpdate.relation) {
      toast.error(
        getTabsLabels?.actions?.relatedProducts?.notifications?.cannotPinProduct ||
          'Cannot pin this product'
      );
      return;
    }

    try {
      // Call API to update the pin status
      const success = await productService.updateRelationship(productId, productToUpdate.id, {
        is_pinned: isPinned,
      });

      if (success) {
        // Update local state and resort
        setRelatedProducts(prev => {
          // First update the pinned status
          const updatedProducts = prev.map(product => {
            if (product.id === productToUpdate.id && product.relation) {
              return {
                ...product,
                relation: {
                  ...product.relation,
                  is_pinned: isPinned,
                },
              };
            }
            return product;
          });

          // Then re-sort based on updated pin status
          return updatedProducts.sort((a, b) => {
            // First sort by pinned status
            if (a.relation?.is_pinned && !b.relation?.is_pinned) return -1;
            if (!a.relation?.is_pinned && b.relation?.is_pinned) return 1;

            // For pinned items, maintain the order by created date (newest first)
            if (a.relation?.is_pinned && b.relation?.is_pinned) {
              return (
                new Date(b.relation.created_at).getTime() -
                new Date(a.relation.created_at).getTime()
              );
            }

            // Then sort by relation existence (explicit relations first)
            if (a.relation && !b.relation) return -1;
            if (!a.relation && b.relation) return 1;

            // Then sort by source (manual first)
            if (a.relation?.source === 'manual' && b.relation?.source !== 'manual') return -1;
            if (a.relation?.source !== 'manual' && b.relation?.source === 'manual') return 1;

            // Finally sort by name
            return (a.name || '').localeCompare(b.name || '');
          });
        });

        toast.success(
          isPinned
            ? getTabsLabels?.actions?.relatedProducts?.notifications?.productPinned ||
                'Product pinned'
            : getTabsLabels?.actions?.relatedProducts?.notifications?.productUnpinned ||
                'Product unpinned'
        );
      } else {
        toast.error(
          isPinned
            ? getTabsLabels?.actions?.relatedProducts?.notifications?.pinFailed ||
                'Failed to pin product'
            : getTabsLabels?.actions?.relatedProducts?.notifications?.unpinFailed ||
                'Failed to unpin product'
        );
      }
    } catch {
      toast.error(
        isPinned
          ? getTabsLabels?.actions?.relatedProducts?.notifications?.pinFailed ||
              'Failed to pin product'
          : getTabsLabels?.actions?.relatedProducts?.notifications?.unpinFailed ||
              'Failed to unpin product'
      );
    }
  };

  if (loading) {
    return (
      <CollapsibleSection
        title={getTabsLabels?.actions?.relatedProducts?.title || 'Related Products'}
        description={
          getTabsLabels?.actions?.relatedProducts?.loadingDescription ||
          'Loading related products...'
        }
        id="related-products-section"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Card key={`skeleton-${index}`} className="w-full">
              <CardContent className="p-4">
                <Skeleton className="mb-4 h-24 w-full" />
                <Skeleton className="mb-2 h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
              <CardFooter className="flex justify-between p-4">
                <Skeleton className="h-4 w-1/3" />
              </CardFooter>
            </Card>
          ))}
        </div>
      </CollapsibleSection>
    );
  }

  // Create the action buttons for the section header
  const actionButtons = (
    <div className="flex space-x-2">
      {error && (
        <Button
          variant="outline"
          size="sm"
          onClick={fetchRelatedProducts}
          className="text-destructive"
        >
          <RefreshCw className="mr-1 size-4" />
          {getTabsLabels?.actions?.relatedProducts?.actions?.tryAgain || 'Try Again'}
        </Button>
      )}

      {relatedProducts.length > maxProductsPerPage && (
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handlePrevPage}
            disabled={activeIndex === 0}
            aria-label={
              getTabsLabels?.actions?.relatedProducts?.pagination?.previousPage || 'Previous page'
            }
          >
            <ChevronLeft className="size-4" />
          </Button>

          <span className="text-sm text-muted-foreground">
            {getTabsLabels?.actions?.relatedProducts?.pagination?.page
              ?.replace('{{current}}', currentPage.toString())
              .replace('{{total}}', totalPages.toString()) ||
              `Page ${currentPage} of ${totalPages}`}
          </span>

          <Button
            size="sm"
            variant="outline"
            onClick={handleNextPage}
            disabled={activeIndex + maxProductsPerPage >= relatedProducts.length}
            aria-label={
              getTabsLabels?.actions?.relatedProducts?.pagination?.nextPage || 'Next page'
            }
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      )}
    </div>
  );

  // Description for the collapsible section
  const sectionDescription =
    relatedProducts.length > 0
      ? getTabsLabels?.actions?.relatedProducts?.description
          ?.replace('{{count}}', relatedProducts.length.toString())
          .replace(/{{plural}}/g, relatedProducts.length !== 1 ? 's' : '') ||
        `${relatedProducts.length} related product${relatedProducts.length !== 1 ? 's' : ''}`
      : undefined;

  return (
    <CollapsibleSection
      title={getTabsLabels?.actions?.relatedProducts?.title || 'Related Products'}
      description={sectionDescription}
      actions={actionButtons}
      defaultOpen={true}
      id="related-products-section"
    >
      <div className="space-y-4">
        <EnhancedAddRelatedProductPanel
          productId={productId}
          relatedProducts={relatedProducts}
          onProductAdded={handleAddRelatedProduct}
          onMultipleProductsAdded={handleAddMultipleRelatedProducts}
        />

        <div className="relative">
          {error ? (
            <div className="rounded-md bg-muted p-8 text-center">
              <p className="text-destructive">
                {error === 'FETCH_FAILED'
                  ? getTabsLabels?.actions?.relatedProducts?.panel?.errorLoading ||
                    'Failed to load related products'
                  : error}
              </p>
            </div>
          ) : relatedProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-md border border-dashed bg-muted/20 p-12">
              <div className="flex max-w-md flex-col items-center space-y-4 text-center">
                <div className="rounded-full bg-primary/10 p-3">
                  <LinkIcon className="size-8 text-primary" />
                </div>
                <h3 className="text-xl font-medium">
                  {getTabsLabels?.actions?.relatedProducts?.emptyState?.title ||
                    'Add your first related product'}
                </h3>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
              {visibleProducts.map(product => (
                <RelatedProductCard
                  key={product.id}
                  product={product}
                  onRemove={() => handleRemoveRelatedProduct(product)}
                  onEdit={product.relation ? () => setupEditRelation(product.relation!) : undefined}
                  onTogglePin={
                    product.relation
                      ? (isPinned: boolean) => handleTogglePin(product, isPinned)
                      : undefined
                  }
                  getTabsLabels={getTabsLabels}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Relation Dialog - Centered on screen */}
      <Dialog open={!!editingRelation} onOpenChange={open => !open && setEditingRelation(null)}>
        {editingRelation && (
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {getTabsLabels?.actions?.relatedProducts?.dialog?.title || 'Edit Relationship'}
              </DialogTitle>
              <DialogDescription>
                {getTabsLabels?.actions?.relatedProducts?.dialog?.description ||
                  'Update how this product relates to the current product. The relationship type helps customers understand product connections.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="relationship-type">
                  {getTabsLabels?.actions?.relatedProducts?.dialog?.labels?.relationshipType ||
                    'Relationship Type'}
                </Label>
                <Select
                  value={relationshipType}
                  onValueChange={(value: RelationshipType) => setRelationshipType(value)}
                >
                  <SelectTrigger id="relationship-type" className="w-full">
                    <SelectValue
                      placeholder={
                        getTabsLabels?.actions?.relatedProducts?.dialog?.placeholders
                          ?.selectRelationshipType || 'Select relationship type'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>
                        {getTabsLabels?.actions?.relatedProducts?.dialog?.labels
                          ?.relationshipType || 'Relationship Type'}
                      </SelectLabel>
                      <SelectItem value="accessory" className="flex items-center gap-2">
                        <PuzzleIcon className="size-4 text-purple-600" />
                        <span>
                          {getTabsLabels?.actions?.relatedProducts?.dialog?.selectOptions
                            ?.accessory || 'Accessory'}
                        </span>
                      </SelectItem>
                      <SelectItem value="variant" className="flex items-center gap-2">
                        <Layers className="size-4 text-indigo-600" />
                        <span>
                          {getTabsLabels?.actions?.relatedProducts?.dialog?.selectOptions
                            ?.variant || 'Variant'}
                        </span>
                      </SelectItem>
                      <SelectItem
                        value="frequently_bought_together"
                        className="flex items-center gap-2"
                      >
                        <ShoppingCart className="size-4 text-green-600" />
                        <span>
                          {getTabsLabels?.actions?.relatedProducts?.dialog?.selectOptions
                            ?.frequently_bought_together || 'Bought Together'}
                        </span>
                      </SelectItem>
                      <SelectItem value="replacement" className="flex items-center gap-2">
                        <RefreshCw className="size-4 text-orange-600" />
                        <span>
                          {getTabsLabels?.actions?.relatedProducts?.dialog?.selectOptions
                            ?.replacement || 'Replacement'}
                        </span>
                      </SelectItem>
                      <SelectItem value="similar" className="flex items-center gap-2">
                        <Copy className="size-4 text-blue-600" />
                        <span>
                          {getTabsLabels?.actions?.relatedProducts?.dialog?.selectOptions
                            ?.similar || 'Similar'}
                        </span>
                      </SelectItem>
                      <SelectItem value="general" className="flex items-center gap-2">
                        <LinkIcon className="size-4 text-slate-600" />
                        <span>
                          {getTabsLabels?.actions?.relatedProducts?.dialog?.selectOptions
                            ?.general || 'General'}
                        </span>
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <p className="mt-1 text-sm text-muted-foreground">
                  {relationshipType === 'accessory' &&
                    (getTabsLabels?.actions?.relatedProducts?.relationshipDescriptions?.accessory ||
                      'Complementary product that enhances the main product')}
                  {relationshipType === 'variant' &&
                    (getTabsLabels?.actions?.relatedProducts?.relationshipDescriptions?.variant ||
                      'Alternative version of the same product')}
                  {relationshipType === 'frequently_bought_together' &&
                    (getTabsLabels?.actions?.relatedProducts?.relationshipDescriptions
                      ?.frequently_bought_together ||
                      'Products often purchased together')}
                  {relationshipType === 'replacement' &&
                    (getTabsLabels?.actions?.relatedProducts?.relationshipDescriptions
                      ?.replacement ||
                      'Product that can replace the current one')}
                  {relationshipType === 'similar' &&
                    (getTabsLabels?.actions?.relatedProducts?.relationshipDescriptions?.similar ||
                      'Product with similar features')}
                  {relationshipType === 'general' &&
                    (getTabsLabels?.actions?.relatedProducts?.relationshipDescriptions?.general ||
                      'General related product')}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">
                  {getTabsLabels?.actions?.relatedProducts?.dialog?.labels?.notes ||
                    'Notes (Optional)'}
                </Label>
                <Textarea
                  id="notes"
                  placeholder={
                    getTabsLabels?.actions?.relatedProducts?.dialog?.placeholders?.addNotes ||
                    'Add any notes about this relationship'
                  }
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  {getTabsLabels?.actions?.relatedProducts?.dialog?.helpText?.notes ||
                    'Notes will appear on the product card and help explain the relationship to customers.'}
                </p>
              </div>
            </div>

            <DialogFooter className="sm:justify-end">
              <Button variant="outline" onClick={() => setEditingRelation(null)} disabled={saving}>
                {getTabsLabels?.actions?.relatedProducts?.dialog?.buttons?.cancel || 'Cancel'}
              </Button>
              <Button onClick={handleUpdateRelationship} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    {getTabsLabels?.actions?.relatedProducts?.dialog?.buttons?.saving ||
                      'Saving...'}
                  </>
                ) : (
                  getTabsLabels?.actions?.relatedProducts?.dialog?.buttons?.saveChanges ||
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </CollapsibleSection>
  );
};

interface RelatedProductCardProps {
  product: EnhancedProduct;
  onRemove: () => void;
  onEdit?: () => void;
  onTogglePin?: (isPinned: boolean) => void;
  getTabsLabels: ReturnType<typeof useProductDetailPageTranslations>['getTabsLabels'];
}

// Helper to get relationship border color for card header
const getRelationshipBorderColor = (relation?: ProductRelation): string => {
  if (!relation) return 'border-slate-300 dark:border-slate-600';

  switch (relation.relationship_type) {
    case 'accessory':
      return 'border-purple-400 dark:border-purple-600';
    case 'variant':
      return 'border-indigo-400 dark:border-indigo-600';
    case 'frequently_bought_together':
      return 'border-green-400 dark:border-green-600';
    case 'replacement':
      return 'border-orange-400 dark:border-orange-600';
    case 'similar':
      return 'border-blue-400 dark:border-blue-600';
    case 'general':
      return 'border-slate-400 dark:border-slate-600';
    default:
      return 'border-slate-400 dark:border-slate-600';
  }
};

const RelatedProductCard: React.FC<RelatedProductCardProps> = ({
  product,
  onEdit,
  onRemove,
  onTogglePin,
  getTabsLabels,
}) => {
  const [hovering, setHovering] = useState(false);
  const [isPinning, setIsPinning] = useState(false);

  // Get organization settings for number formatting
  const { orgSettings } = useOrgSettings();
  const numberFormat = (orgSettings?.number_format || 'dot') as NumberFormat;
  const dateFormat = (orgSettings?.date_format || 'MDY') as DateFormat;

  // Helper function to get price display using centralized utility
  const getPriceDisplay = () => {
    const defaultCurrency = orgSettings?.default_currency?.iso_code || 'USD';
    return formatProductPrice(product, numberFormat, defaultCurrency);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onEdit?.();
    }
  };

  const isManuallyAdded = product.relation?.source === 'manual';
  const isAutoSuggested = !!product.relation && product.relation.source === 'algorithm';
  const isImplicitRelation = !product.relation; // No relation = implicitly related (by category)
  const isPinned = product.relation?.is_pinned || false;

  const tooltipText = isManuallyAdded
    ? `Added manually on ${formatDateForDisplay(product.relation!.created_at, dateFormat)}`
    : isAutoSuggested
      ? 'Algorithmic suggestion'
      : `Matched by category: ${product.category}`;

  const handleTogglePin = async () => {
    if (!product.relation || !onTogglePin) return;

    setIsPinning(true);
    try {
      await onTogglePin(!isPinned);
    } finally {
      setIsPinning(false);
    }
  };

  return (
    <TooltipProvider>
      <Card
        className={`
          relative overflow-hidden transition-all duration-200
          ${isManuallyAdded ? 'border-primary/20' : ''}
          ${isAutoSuggested ? 'bg-blue-50/30 dark:bg-blue-950/20' : ''}
          ${isImplicitRelation ? 'bg-muted/30' : ''}
          ${isPinned ? 'ring-2 ring-yellow-300 dark:ring-yellow-500/50' : ''}
          ${hovering ? 'scale-[1.01] shadow-md' : ''}
          ${product.relation ? getRelationshipBorderColor(product.relation) : ''}
        `}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        {/* Relationship type badge - always visible at top */}
        {product.relation?.relationship_type && (
          <div className="absolute left-1/2 top-2 z-10 -translate-x-1/2">
            <Badge
              variant="outline"
              className={`${getRelationshipTypeStyle(product.relation.relationship_type)} flex min-w-[110px] items-center justify-center gap-1 whitespace-nowrap px-3`}
            >
              {getRelationshipTypeIcon(product.relation.relationship_type)}
              <span>
                {formatRelationshipType(product.relation.relationship_type, getTabsLabels)}
              </span>
            </Badge>
          </div>
        )}

        {/* Pinned indicator - small icon in top left */}
        {isPinned && (
          <div className="absolute left-2 top-2 z-20">
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="outline"
                  className="flex size-6 items-center justify-center border-yellow-300 bg-yellow-100 p-0 text-yellow-800 dark:border-yellow-700/40 dark:bg-yellow-900/40 dark:text-yellow-300"
                >
                  <Pin className="size-3" />
                  <span className="sr-only">Pinned</span>
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="right">
                {getTabsLabels?.actions?.relatedProducts?.tooltips?.pinned ||
                  'Pinned product - displays at the top of the list'}
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Source badges - moved to align properly with pin badge */}
        <div className={`absolute top-2 ${isPinned ? 'left-10' : 'left-2'} flex flex-col gap-1`}>
          {isManuallyAdded && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="gap-1 border-primary/30">
                  <HandIcon className="size-3" />
                  <span className="sr-only md:not-sr-only">
                    {getTabsLabels?.actions?.relatedProducts?.panel?.sourceBadges?.manual ||
                      'Manual'}
                  </span>
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="right">{tooltipText}</TooltipContent>
            </Tooltip>
          )}

          {isAutoSuggested && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="secondary"
                  className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                >
                  <Sparkles className="mr-1 size-3" />
                  <span>
                    {getTabsLabels?.actions?.relatedProducts?.panel?.sourceBadges?.algorithm ||
                      'Suggested'}
                  </span>
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="right">{tooltipText}</TooltipContent>
            </Tooltip>
          )}

          {isImplicitRelation && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="bg-muted/70">
                  <Tag className="mr-1 size-3" />
                  <span>
                    {getTabsLabels?.actions?.relatedProducts?.panel?.sourceBadges?.implicit ||
                      'Category Match'}
                  </span>
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="right">{tooltipText}</TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Quick actions on hover */}
        {hovering && !isImplicitRelation && (
          <div className="absolute bottom-2 right-2 z-10 flex gap-1 duration-150 animate-in fade-in">
            {onTogglePin && product.relation && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className={`size-7 ${isPinned ? 'border-yellow-200 bg-yellow-100 hover:bg-yellow-200 dark:border-yellow-800/40 dark:bg-yellow-900/30' : ''}`}
                    onClick={handleTogglePin}
                    disabled={isPinning}
                  >
                    {isPinning ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : isPinned ? (
                      <PinOff className="size-3.5" />
                    ) : (
                      <Pin className="size-3.5" />
                    )}
                    <span className="sr-only">{isPinned ? 'Unpin' : 'Pin'} related product</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {isPinned
                    ? getTabsLabels?.actions?.relatedProducts?.tooltips?.unpinProduct ||
                      'Unpin product'
                    : getTabsLabels?.actions?.relatedProducts?.tooltips?.pinProduct ||
                      'Pin product to show first'}
                </TooltipContent>
              </Tooltip>
            )}

            {onEdit && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="size-7"
                    onClick={onEdit}
                    onKeyDown={handleKeyDown}
                  >
                    <Edit2 className="size-3.5" />
                    <span className="sr-only">Edit relationship</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {getTabsLabels?.actions?.relatedProducts?.tooltips?.editRelationship ||
                    'Edit relationship type and notes'}
                </TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="size-7 text-destructive hover:bg-destructive/10"
                  onClick={onRemove}
                >
                  <Trash2 className="size-3.5" />
                  <span className="sr-only">Remove related product</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {getTabsLabels?.actions?.relatedProducts?.tooltips?.removeProduct ||
                  'Remove related product'}
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Card Content */}
        <Link to={`/app/products/${product.id}`} className="block">
          <CardContent className="p-4 pt-12">
            <div className="mb-3 flex aspect-square items-center justify-center rounded-md bg-accent">
              {(() => {
                const imageUrl = pickPrimaryImage(product, 'medium');

                return imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={product.name}
                    className="size-full object-contain p-2"
                    onError={e => {
                      logError(
                        'Failed to load image for related product',
                        {
                          component: 'RelatedProductsPanel',
                          action: 'imageOnError',
                          metadata: {
                            productName: product.name,
                            productId: product.id,
                            imageUrl,
                          },
                        },
                        { showToast: false, userFacing: false }
                      );
                      // Try fallback sources
                      const target = e.target as HTMLImageElement;
                      const primaryAssetUrl = (product as Product & { primary_asset_url?: string })
                        .primary_asset_url;
                      const primaryImageThumb = (
                        product as Product & { primary_image_thumb?: string }
                      ).primary_image_thumb;

                      if (imageUrl !== primaryAssetUrl && primaryAssetUrl) {
                        target.src = primaryAssetUrl;
                      } else if (imageUrl !== primaryImageThumb && primaryImageThumb) {
                        target.src = primaryImageThumb;
                      } else {
                        // Show placeholder
                        target.style.display = 'none';
                        target.parentElement!.innerHTML = `<div class="text-muted-foreground text-xs">${getTabsLabels?.actions?.relatedProducts?.states?.noImage || 'No image'}</div>`;
                      }
                    }}
                  />
                ) : (
                  <div className="text-xs text-muted-foreground">
                    {getTabsLabels?.actions?.relatedProducts?.states?.noImage || 'No image'}
                  </div>
                );
              })()}
            </div>

            <div className="space-y-1">
              <h4 className="line-clamp-2 font-medium leading-tight">{product.name}</h4>
              <div className="text-sm text-muted-foreground">SKU: {product.sku}</div>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col p-4 pt-0">
            <div className="flex w-full justify-between">
              <div className="text-sm font-medium">{getPriceDisplay()}</div>
            </div>

            {/* Display notes if available */}
            {product.relation?.notes && (
              <div className="mt-2 w-full border-t border-dashed border-muted pt-2">
                <p className="line-clamp-2 text-xs text-muted-foreground">
                  {product.relation.notes.length > 50
                    ? product.relation.notes.substring(0, 50) + '...'
                    : product.relation.notes}
                </p>
              </div>
            )}
          </CardFooter>
        </Link>
      </Card>
    </TooltipProvider>
  );
};

export default RelatedProductsPanel;
